import { useState } from 'react';
import { Link } from 'react-router-dom';

interface Worksheet {
  title: string;
  topic: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  pages: number;
}

const worksheets: Worksheet[] = [
  { title: 'Evaluating Limits', topic: 'Limits & Continuity', difficulty: 'Medium', pages: 3 },
  { title: 'Continuity & IVT', topic: 'Limits & Continuity', difficulty: 'Medium', pages: 3 },
  { title: 'Limit Definition of Derivative', topic: 'Differentiation', difficulty: 'Medium', pages: 3 },
  { title: 'Basic Differentiation Rules', topic: 'Differentiation', difficulty: 'Easy', pages: 3 },
  { title: 'Chain Rule & Implicit Differentiation', topic: 'Advanced Differentiation', difficulty: 'Hard', pages: 4 },
  { title: 'Inverse & Log Differentiation', topic: 'Advanced Differentiation', difficulty: 'Hard', pages: 4 },
  { title: 'Particle Motion & Related Rates', topic: 'Applications of Derivatives', difficulty: 'Hard', pages: 4 },
  { title: "L'Hôpital's Rule", topic: 'Applications of Derivatives', difficulty: 'Medium', pages: 3 },
  { title: 'MVT & Optimization', topic: 'Analytical Applications', difficulty: 'Hard', pages: 4 },
  { title: 'Curve Sketching', topic: 'Analytical Applications', difficulty: 'Medium', pages: 3 },
  { title: 'Antiderivatives & FTC', topic: 'Integration', difficulty: 'Medium', pages: 3 },
  { title: 'U-Substitution', topic: 'Integration', difficulty: 'Hard', pages: 4 },
  { title: 'Slope Fields & Separable DEs', topic: 'Differential Equations', difficulty: 'Hard', pages: 4 },
  { title: 'Growth & Decay Models', topic: 'Differential Equations', difficulty: 'Medium', pages: 3 },
  { title: 'Area Between Curves', topic: 'Applications of Integration', difficulty: 'Medium', pages: 3 },
  { title: 'Volumes of Revolution', topic: 'Applications of Integration', difficulty: 'Hard', pages: 4 },
  { title: 'Parametric Equations', topic: 'Parametric & Vectors', difficulty: 'Hard', pages: 4 },
  { title: 'Vector-Valued Functions', topic: 'Parametric & Vectors', difficulty: 'Hard', pages: 4 },
  { title: 'Polar Coordinates & Area', topic: 'Polar Curves', difficulty: 'Hard', pages: 4 },
  { title: 'Polar Derivatives & Arc Length', topic: 'Polar Curves', difficulty: 'Hard', pages: 3 },
  { title: 'Sequences & Convergence Tests', topic: 'Infinite Series', difficulty: 'Hard', pages: 4 },
  { title: 'Ratio/Root Tests & Power Series', topic: 'Infinite Series', difficulty: 'Hard', pages: 4 },
  { title: 'Taylor & Maclaurin Series', topic: 'Taylor Series', difficulty: 'Hard', pages: 4 },
  { title: 'Applications of Series', topic: 'Taylor Series', difficulty: 'Hard', pages: 3 },
];

const scheduleOptions = [
  { weeks: 6, label: '6-Week Intensive', description: 'Fast AP exam prep' },
  { weeks: 8, label: '8-Week Standard', description: 'Recommended pace' },
  { weeks: 12, label: '12-Week Thorough', description: 'Deep mastery' },
  { weeks: 16, label: '16-Week Full Course', description: 'Semester review' },
];

const studyTips = [
  {
    icon: 'ri-functions',
    title: 'AB + BC Extra Topics',
    description: 'BC includes all AB content plus parametric, polar, vectors, and infinite series. Master AB topics thoroughly before moving to BC-exclusive material.',
  },
  {
    icon: 'ri-infinity',
    title: 'Series is the Most Tested BC Topic',
    description: 'Taylor/Maclaurin series, ratio test, interval of convergence — these appear every year on the BC exam. Spend extra time here.',
  },
  {
    icon: 'ri-compass-3-line',
    title: 'Polar & Parametric Require Visualization',
    description: 'Practice sketching polar curves and parametric paths. Understanding the geometry helps with derivatives, arc length, and area problems.',
  },
  {
    icon: 'ri-calculator-line',
    title: 'Euler\'s Method & Logistic Growth',
    description: 'These BC-specific DE topics are heavily tested. Practice the step-by-step Euler method and know the logistic growth model properties.',
  },
];

export default function APCalcBCStudyPlanPage() {
  const [selectedWeeks, setSelectedWeeks] = useState(8);

  const worksheetsPerWeek = Math.ceil(worksheets.length / selectedWeeks);
  const totalPages = worksheets.reduce((sum, w) => sum + w.pages, 0);
  const estimatedHours = Math.round((totalPages * 0.6 + worksheets.length * 0.5) * 10) / 10;

  const getWeeklySchedule = () => {
    const schedule: Worksheet[][] = [];
    for (let i = 0; i < selectedWeeks; i++) {
      const start = i * worksheetsPerWeek;
      const end = Math.min(start + worksheetsPerWeek, worksheets.length);
      const weekItems = worksheets.slice(start, end);
      if (weekItems.length > 0) schedule.push(weekItems);
    }
    return schedule;
  };

  const weeklySchedule = getWeeklySchedule();

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50">
      <header className="bg-white border-b border-teal-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <Link to="/" className="inline-flex items-center text-teal-600 hover:text-teal-700 mb-3 text-sm font-medium whitespace-nowrap">
                <i className="ri-arrow-left-line mr-2"></i>Back to Home
              </Link>
              <h1 className="text-4xl font-bold text-gray-900">AP Calculus BC Study Plan</h1>
              <p className="text-lg text-gray-600 mt-2">10 Units · All AB + Parametric, Polar, Series</p>
            </div>
            <button onClick={() => window.print()} className="hidden sm:flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap print:hidden">
              <i className="ri-printer-line"></i>Print Plan
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl border border-teal-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Choose Your Study Timeline</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {scheduleOptions.map((option) => (
              <button key={option.weeks} onClick={() => setSelectedWeeks(option.weeks)}
                className={`p-6 rounded-xl border-2 transition-all text-left cursor-pointer ${selectedWeeks === option.weeks ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-teal-300'}`}>
                <div className="text-3xl font-bold text-teal-600 mb-2">{option.weeks}</div>
                <div className="font-semibold text-gray-900 mb-1">{option.label}</div>
                <div className="text-sm text-gray-600">{option.description}</div>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8 pt-8 border-t border-teal-100">
            <div className="text-center"><div className="text-3xl font-bold text-teal-600">{worksheets.length}</div><div className="text-sm text-gray-600 mt-1">Total Worksheets</div></div>
            <div className="text-center"><div className="text-3xl font-bold text-teal-600">{totalPages}</div><div className="text-sm text-gray-600 mt-1">Total Pages</div></div>
            <div className="text-center"><div className="text-3xl font-bold text-teal-600">{estimatedHours}h</div><div className="text-sm text-gray-600 mt-1">Estimated Time</div></div>
          </div>
        </div>

        <div className="space-y-6 mb-8">
          {weeklySchedule.map((week, weekIndex) => {
            const weekPages = week.reduce((sum, w) => sum + w.pages, 0);
            return (
              <div key={weekIndex} className="bg-white rounded-xl border border-teal-100 overflow-hidden">
                <div className="bg-gradient-to-r from-teal-500 to-cyan-500 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-white">Week {weekIndex + 1}</h3>
                    <div className="flex items-center gap-6 text-white text-sm">
                      <span><i className="ri-file-list-line mr-1"></i>{week.length} worksheets</span>
                      <span><i className="ri-pages-line mr-1"></i>{weekPages} pages</span>
                    </div>
                  </div>
                </div>
                <div className="p-6 space-y-3">
                  {week.map((worksheet, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-teal-50/50 rounded-lg border border-teal-100">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center font-semibold text-sm flex-shrink-0">{idx + 1}</div>
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{worksheet.title}</div>
                          <div className="text-sm text-gray-600 mt-0.5">{worksheet.topic}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${worksheet.difficulty === 'Easy' ? 'bg-green-100 text-green-700' : worksheet.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>{worksheet.difficulty}</span>
                        <span className="text-sm text-gray-600 whitespace-nowrap">{worksheet.pages}p</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-xl border border-teal-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">AP Calculus BC Study Tips</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {studyTips.map((tip, index) => (
              <div key={index} className="flex gap-4">
                <div className="w-12 h-12 rounded-lg bg-teal-100 text-teal-600 flex items-center justify-center flex-shrink-0">
                  <i className={`${tip.icon} text-2xl`}></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">{tip.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{tip.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link to="/ap-calc-bc" className="bg-white rounded-xl border border-teal-100 p-6 hover:border-teal-300 transition-all group">
            <div className="w-12 h-12 rounded-lg bg-teal-100 text-teal-600 flex items-center justify-center mb-4 group-hover:bg-teal-600 group-hover:text-white transition-colors">
              <i className="ri-book-open-line text-2xl"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">BC Curriculum</h3>
            <p className="text-sm text-gray-600">Full lessons with 640+ practice problems</p>
          </Link>
          <Link to="/ap-calc-bc-progress" className="bg-white rounded-xl border border-teal-100 p-6 hover:border-teal-300 transition-all group">
            <div className="w-12 h-12 rounded-lg bg-teal-100 text-teal-600 flex items-center justify-center mb-4 group-hover:bg-teal-600 group-hover:text-white transition-colors">
              <i className="ri-bar-chart-box-line text-2xl"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">Progress Tracker</h3>
            <p className="text-sm text-gray-600">Track completed topics and skills</p>
          </Link>
          <Link to="/ap-calc-ab" className="bg-white rounded-xl border border-teal-100 p-6 hover:border-teal-300 transition-all group">
            <div className="w-12 h-12 rounded-lg bg-teal-100 text-teal-600 flex items-center justify-center mb-4 group-hover:bg-teal-600 group-hover:text-white transition-colors">
              <i className="ri-arrow-left-circle-line text-2xl"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">AP Calculus AB</h3>
            <p className="text-sm text-gray-600">Review foundational AB content</p>
          </Link>
        </div>
      </main>
    </div>
  );
}
